import { Component, inject, Input } from '@angular/core';
import { ShowPerformance } from '../../../models/showperformance.model';
import { AlertController, NavController } from '@ionic/angular';
import { CrewRole } from '../../crew-role';
import { ReadStateService } from '../../../services/read-state.service';
import { AuthStateService } from '../../auth/auth-state.service';

@Component({
    selector: 'app-log-button',
    templateUrl: './log-button.component.html',
    styleUrls: ['./log-button.component.scss'],
    standalone: false
})
export class LogButtonComponent {

  @Input() performance: ShowPerformance;
  @Input() public activeUserId: number | null = null;
  protected readonly CrewRole = CrewRole;
  private readonly navController: NavController = inject(NavController);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly readState: ReadStateService = inject(ReadStateService);
  private readonly authStateService: AuthStateService = inject(AuthStateService);

  /**
   * True when this log has been created/edited by someone else since the user
   * last opened it (device-local, baseline = first use). Admin-only for now
   * while the feature is being validated in production.
   */
  public get hasUnseenChange(): boolean {
    if (this.authStateService.user()?.isAdmin() !== true) {
      return false;
    }
    const log = this.performance?.log;
    if (!log?.id) {
      return false;
    }
    // updatedBy is only set on edit, so fall back to submittedBy (the creator)
    // — a log you created yourself shouldn't flag. Activity = latest of the
    // create/update times, so newly-created logs surface too.
    const activityMs = Math.max(this.readState.toMs(log.updatedAt), this.readState.toMs(log.createdAt));
    const actorId = log.updatedBy?.id ?? log.submittedBy?.id;
    return this.readState.isUnseen('log', log.id, activityMs, actorId, this.activeUserId);
  }

  public async showLog(): Promise<void> {
    if (this.hasLog()) {
      await this.navigateToLogDetails();
    } else {
      await this.showAddLogAlert();
    }
  }

  public isLogConcept(): boolean {
    return this.performance?.log?.status === 'CONCEPT';
  }

  public hasLog(): boolean {
    return !(
      this.performance?.log === null || this.performance?.log === undefined
    );
  }

  public async navigateToLogDetails(): Promise<void> {
    if (this.performance.log?.id) {
      // ?back=/dashboard so the details page (and any edit→save→details
      // redirect along the way) knows where the user originally came from.
      const queryParams = { back: '/dashboard' };
      if (this.activeUserId === this.performance.log.toneelmeester?.id && this.performance.log.status === 'CONCEPT') {
        await this.navController.navigateForward(['/logboek', 'edit', this.performance.log.id], { queryParams });
      } else {
        await this.navController.navigateForward(['/logboek', this.performance.log.id], { queryParams });
      }
    }
  }

  public async showAddLogAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Nieuwe log aanmaken?',
      message:
        'Er is (nog) geen log gemaakt voor dit speelmoment. Wil je deze aanmaken?',
      buttons: [
        {
          text: 'Nee',
          role: 'cancel'
        },
        {
          text: 'Ja',
          role: 'confirm',
          handler: async (): Promise<void> => {
            await this.navigateToAddLog();
          }
        }
      ]
    });
    await alert.present();
  }

  public async navigateToAddLog(): Promise<void> {
    if (this.performance?.id) {
      await this.navController.navigateForward(['/logboek/add'], {
        queryParams: { performanceId: this.performance.id, back: '/dashboard' }
      });
    }
  }

}
